import React from 'react';
import styled from '@emotion/styled';
import { palette, spacing, typography } from '../../style-utils/theme';
import {
  Clock,
  bothClocks,
  clockNote,
} from '../../function-utils/building-time';
import { useDeskInPlan } from '../hooks/use-desk-in-plan';
import { w } from '../../stays/wording';

/**
 * WHICH CLOCK THIS SCREEN IS ON — "Your time — Budapest" or "Building time —
 * Toronto" (R-T12: no time is shown with the reader left to guess).
 *
 * One per screen or card, never one per row. The `data-*` attributes are what
 * the audience-clock audit and the time journeys read, so a screen that shows a
 * time and no note fails them.
 *
 * `label` is a zone label the SERVER sent (X-Report-Timezone-Label,
 * timezone_label): printed verbatim in place of a derived one.
 */

const Note = styled.p<{ $inline?: boolean }>`
  display: ${({ $inline }) => ($inline ? 'inline' : 'block')};
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.medium};
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
  overflow-wrap: anywhere;
`;

const Both = styled.span`
  display: block;
  margin-top: ${spacing.half};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
  overflow-wrap: anywhere;
`;

export interface ClockNoteProps {
  clock: Clock;
  /** A zone label the server sent, printed verbatim. */
  label?: string | null;
  /** Text after the note, in the same line ("· desk day 7 Oct"). */
  suffix?: string;
  inline?: boolean;
  className?: string;
  id?: string;
}

export const ClockNote: React.FC<ClockNoteProps> = ({
  clock,
  label,
  suffix,
  inline,
  className,
  id,
}) => {
  // Independent Stays IS2: "Property time" in an organisation without Desk.
  const place = w('Building', useDeskInPlan()) as 'Building' | 'Property';

  return (
    <Note
      as={inline ? 'span' : 'p'}
      $inline={inline}
      id={id}
      className={className}
      data-clock-note=""
      data-clock-whose={clock.whose}
      data-clock-zone={clock.zone}
      data-clock-missing={clock.missing || undefined}
    >
      {clockNote(clock, label, place)}
      {suffix ? ` ${suffix}` : ''}
    </Note>
  );
};

/**
 * THE SECOND LINE OF A RECORD'S DETAIL — "20:05 your time · 14:05 in Toronto".
 * Renders nothing when the reader is already on the building's clock.
 */
export const BothClocks: React.FC<{
  iso: string | null | undefined;
  clock: Clock;
  buildingZone: string | null | undefined;
  className?: string;
}> = ({ iso, clock, buildingZone, className }) => {
  const line = bothClocks(iso, clock, buildingZone);
  if (!line) return null;

  return (
    <Both className={className} data-both-clocks="" data-instant={iso}>
      {line}
    </Both>
  );
};
